import React from 'react';
import {
  ALLERGY_OPTIONS,
  COST_OPTIONS,
  DIETARY_OPTIONS,
  MEAL_OPTIONS,
  PROTEIN_OPTIONS,
  SPICE_LABELS,
  VARIETY_OPTIONS,
  WEEKLY_MEAL_COUNT_OPTIONS
} from './menuPreferencesConstants';
import useCuisineOptions from '../hooks/useCuisineOptions';
import './MenuPreferencesFields.css';

export function SpiceLevelPicker({ value, onChange, disabled = false }) {
  const level = Number(value) || 3;
  return (
    <div className="spice-picker">
      <div className="spice-picker-buttons">
        {[1, 2, 3, 4, 5].map((item) => (
          <button
            key={item}
            type="button"
            className={`spice-picker-btn ${item <= level ? 'active' : ''}`}
            onClick={() => onChange(item)}
            disabled={disabled}
            aria-label={`Spice level ${item} of 5`}
            aria-pressed={item === level}
          >
            <i className="fas fa-pepper-hot" />
          </button>
        ))}
      </div>
      <div className="spice-picker-meta">
        <span>Mild</span>
        <span className="spice-picker-current">{SPICE_LABELS[level - 1]}</span>
        <span>Spicy</span>
      </div>
      <p className="spice-picker-hint">Tap a pepper to set heat. Tap a lower one to reduce it.</p>
    </div>
  );
}

function samePreference(left, right) {
  return String(left).trim().toLowerCase() === String(right).trim().toLowerCase();
}

function ChipGrid({ options, selected, onToggle, normalize = (value) => value }) {
  return (
    <div className="menu-pref-chips">
      {options.map((option) => {
        const value = normalize(option);
        const isSelected = selected.some((item) => samePreference(item, value) || samePreference(item, option));
        return (
          <button
            key={option}
            type="button"
            className={`menu-pref-chip ${isSelected ? 'selected' : ''}`}
            aria-pressed={isSelected}
            onClick={() => onToggle(value)}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}

const MenuPreferencesFields = ({
  preferences,
  onChange,
  dietaryOptions = DIETARY_OPTIONS,
  cuisineOptions = null,
  showWeeklyEmail = true
}) => {
  const { cuisines: fetchedCuisines } = useCuisineOptions();
  const baseCuisines = cuisineOptions || fetchedCuisines;
  // Keep already-saved cuisines visible even if no approved recipe uses them
  // anymore, so the user can still see and clear the selection.
  const selectedCuisines = preferences.cuisinePreferences || [];
  const knownCuisines = new Set(baseCuisines.map((value) => value.toLowerCase()));
  const cuisines = [
    ...baseCuisines,
    ...selectedCuisines.filter((value) => !knownCuisines.has(String(value).toLowerCase())).sort()
  ];

  const toggleArray = (field, value) => {
    const current = preferences[field] || [];
    const matches = (item) => samePreference(item, value);
    const next = current.some(matches)
      ? current.filter((item) => !matches(item))
      : [...current, value];
    onChange(field, next);
  };

  return (
    <div className="menu-pref-fields">
      <div className="menu-pref-section">
        <h3>Dietary preferences</h3>
        <ChipGrid
          options={dietaryOptions}
          selected={preferences.dietaryPreferences || []}
          onToggle={(value) => toggleArray('dietaryPreferences', value)}
        />
      </div>

      <div className="menu-pref-section">
        <h3>Allergies</h3>
        <ChipGrid
          options={ALLERGY_OPTIONS}
          selected={preferences.allergies || []}
          onToggle={(value) => toggleArray('allergies', value.toLowerCase())}
          normalize={(value) => value.toLowerCase()}
        />
      </div>

      <div className="menu-pref-section">
        <h3>Cuisine preferences</h3>
        <ChipGrid
          options={cuisines}
          selected={preferences.cuisinePreferences || []}
          onToggle={(value) => toggleArray('cuisinePreferences', value)}
        />
      </div>

      <div className="menu-pref-section">
        <h3>Budget</h3>
        <div className="menu-pref-choice-row">
          {COST_OPTIONS.map((option) => (
            <label key={option.value} className={`menu-pref-choice ${preferences.costRange === option.value ? 'selected' : ''}`}>
              <input
                type="radio"
                name="menu-pref-cost"
                value={option.value}
                checked={preferences.costRange === option.value}
                onChange={() => onChange('costRange', option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="menu-pref-section">
        <h3>Spice level</h3>
        <SpiceLevelPicker
          value={preferences.spiceLevel}
          onChange={(level) => onChange('spiceLevel', level)}
        />
      </div>

      <div className="menu-pref-section">
        <h3>Protein preferences</h3>
        <ChipGrid
          options={PROTEIN_OPTIONS}
          selected={preferences.proteinPreferences || []}
          onToggle={(value) => toggleArray('proteinPreferences', value.toLowerCase())}
          normalize={(value) => value.toLowerCase()}
        />
      </div>

      <div className="menu-pref-section">
        <h3>Meals to include</h3>
        <div className="menu-pref-choice-row">
          {MEAL_OPTIONS.map((option) => (
            <label key={option.value} className={`menu-pref-choice ${preferences.mealsToConsider?.includes(option.value) ? 'selected' : ''}`}>
              <input
                type="checkbox"
                checked={preferences.mealsToConsider?.includes(option.value) || false}
                onChange={() => toggleArray('mealsToConsider', option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="menu-pref-section">
        <h3>Meal variety</h3>
        <div className="menu-pref-variety">
          {VARIETY_OPTIONS.map((option) => (
            <label key={option.value} className={`menu-pref-choice stacked ${preferences.mealVariety === option.value ? 'selected' : ''}`}>
              <input
                type="radio"
                name="menu-pref-variety"
                value={option.value}
                checked={preferences.mealVariety === option.value}
                onChange={() => onChange('mealVariety', option.value)}
              />
              <span>
                <strong>{option.label}</strong>
                <small>{option.description}</small>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="menu-pref-section">
        <h3>Additional options</h3>
        <div className="menu-pref-choice-row wrap">
          <label className={`menu-pref-choice ${preferences.mealPrepRecipes ? 'selected' : ''}`}>
            <input
              type="checkbox"
              checked={Boolean(preferences.mealPrepRecipes)}
              onChange={(e) => onChange('mealPrepRecipes', e.target.checked)}
            />
            <span>Meal prep friendly</span>
          </label>
          <label className={`menu-pref-choice ${preferences.lowCarb ? 'selected' : ''}`}>
            <input
              type="checkbox"
              checked={Boolean(preferences.lowCarb)}
              onChange={(e) => onChange('lowCarb', e.target.checked)}
            />
            <span>Low carb</span>
          </label>
          <label className={`menu-pref-choice ${preferences.highProtein ? 'selected' : ''}`}>
            <input
              type="checkbox"
              checked={Boolean(preferences.highProtein)}
              onChange={(e) => onChange('highProtein', e.target.checked)}
            />
            <span>High protein</span>
          </label>
        </div>
      </div>

      <div className="menu-pref-section">
        <h3>Recipes per week</h3>
        <p className="menu-pref-hint">
          How many recipe ideas we suggest for the week ahead.
        </p>
        <div className="menu-pref-choice-row wrap">
          {WEEKLY_MEAL_COUNT_OPTIONS.map((count) => (
            <label
              key={count}
              className={`menu-pref-choice ${(preferences.weeklyMealCount || 7) === count ? 'selected' : ''}`}
            >
              <input
                type="radio"
                name="menu-pref-weekly-count"
                value={count}
                checked={(preferences.weeklyMealCount || 7) === count}
                onChange={() => onChange('weeklyMealCount', count)}
              />
              <span>{count} recipes</span>
            </label>
          ))}
        </div>
      </div>

      {showWeeklyEmail && (
        <div className="menu-pref-section">
          <label className={`menu-pref-weekly ${preferences.weeklyEmailEnabled ? 'selected' : ''}`}>
            <input
              type="checkbox"
              checked={preferences.weeklyEmailEnabled !== false}
              onChange={(e) => onChange('weeklyEmailEnabled', e.target.checked)}
            />
            <span>
              <strong>Send me weekly meal plan ideas</strong>
              <small>We’ll email a week of recipes every Sunday. You can turn this off anytime.</small>
            </span>
          </label>
        </div>
      )}
    </div>
  );
};

export default MenuPreferencesFields;
